/*
  The game page's language buckets (docs/specs/pop-language-split.md D1-D6).

  A set's language is the v2 catalog's free-text `language`, and it is untidy: Pokémon
  alone has Japanese, English, Chinese, Korean, German, Spanish and 316 rows with nothing
  at all. A visitor does not want seven buttons, two of them for a handful of sets, so the
  page offers four buckets and files every set in exactly one:

    - English, Japanese and Chinese by name, case and stray spaces ignored (D1);
    - Other for every language that is none of those three (D1);
    - a set with no language goes in English, unless its name says `(JPN Release)` — the
      catalogue's own way of marking a Japanese row it never gave a language (D3).

  Everything here is pure and unit-tested. The storage helpers are the one exception, and
  they are wrapped so a private window or blocked site data reads as "nothing saved"
  rather than throwing (D6).
*/

export type LangBucket = "en" | "ja" | "zh" | "other";

/* Button order, and the order `bucketsPresent` reports in (D2). */
export const LANG_BUCKETS: readonly LangBucket[] = ["en", "ja", "zh", "other"];

export const LANG_LABELS: Record<LangBucket, string> = {
  en: "English",
  ja: "Japanese",
  zh: "Chinese",
  other: "Other",
};

export const LANG_STORAGE_KEY = "pcg.pop.lang";

/* The URL param and the saved choice are both free text from outside; only these pass. */
export const isLangBucket = (value: unknown): value is LangBucket =>
  typeof value === "string" && (LANG_BUCKETS as readonly string[]).includes(value);

export interface LanguagedSet {
  language?: string | null;
  setName: string;
}

const JPN_RELEASE = /\(JPN Release\)/i;
const NAME_SUFFIX = {
  ja: /\(Japanese\)\s*(\(?FALSE\)?)?\s*$/i,
  zh: /\(Chinese\)\s*(\(?FALSE\)?)?\s*$/i,
  other: /\(Korean\)\s*(\(?FALSE\)?)?\s*$/i,
};

export const bucketOf = (set: LanguagedSet): LangBucket => {
  const language = (set.language ?? "").trim().toLowerCase();
  if (language === "") {
    /*
      The name is the only signal left. "(JPN Release)" was Robert's call (D3); the same
      reading is applied to a trailing "(Japanese)", "(Chinese)" or "(Korean)", which smoke
      testing found on four Pokémon sets with no language ("S&V: Destined Rivals (Korean)"
      sat in English beside the real English set).
    */
    if (JPN_RELEASE.test(set.setName) || NAME_SUFFIX.ja.test(set.setName)) {
      return "ja";
    }
    if (NAME_SUFFIX.zh.test(set.setName)) {
      return "zh";
    }
    if (NAME_SUFFIX.other.test(set.setName)) {
      return "other";
    }
    return "en";
  }
  if (language === "english") {
    return "en";
  }
  if (language === "japanese") {
    return "ja";
  }
  if (language === "chinese") {
    return "zh";
  }
  return "other";
};

/*
  The buckets a game has, in button order. The caller passes LIVE sets only: a bucket made
  of nothing but archived sets would be a button that opens an empty list (D2).
*/
export const bucketsPresent = (sets: readonly LanguagedSet[]): LangBucket[] => {
  const found = new Set(sets.map(bucketOf));
  return LANG_BUCKETS.filter(bucket => found.has(bucket));
};

export interface InitialBucketInput {
  urlParam: string | null | undefined;
  saved: string | null | undefined;
  isJapan: boolean;
  present: readonly LangBucket[];
  /* Cards graded per bucket, for the last-resort pick in D5. */
  cardsByBucket?: Partial<Record<LangBucket, number>>;
  /*
    Whether `present` is final. While catalogue pages are still arriving, a wished-for
    bucket that has not shown up YET is kept rather than replaced by English: otherwise a
    Japanese visitor sees English flash up and then flip once the first Japanese set lands.
    D5's fallback is only taken once the whole catalogue is in. Omitted means final.
  */
  complete?: boolean;
  /*
    The buckets the game's own `languages` list names (GameSummaryDto), when known. A wish
    the game does not even claim is not worth waiting for: a Japanese visitor on Magic, or
    any default-English visitor on Weiss Schwarz, would otherwise sit on skeletons for the
    whole catalogue walk before the page flipped to a language it actually has.
  */
  declared?: readonly LangBucket[];
}

/*
  D4 then D5. The visitor's wish is URL, then saved choice, then Japan, then English —
  first match wins, and an unrecognised value simply falls through to the next source.

  The wish is only honoured if the game has that bucket. When it does not, English is
  shown, and when English is missing too, the bucket with the most cards graded (ties go to
  button order). This returns what to SHOW; it never feeds back into storage, so a Japanese
  visitor on an English-only game is still Japanese on the next game (D5).
*/
export const resolveInitialBucket = ({
  urlParam,
  saved,
  isJapan,
  present,
  cardsByBucket = {},
  complete = true,
  declared,
}: InitialBucketInput): LangBucket => {
  const wish: LangBucket = isLangBucket(urlParam)
    ? urlParam
    : isLangBucket(saved)
      ? saved
      : isJapan
        ? "ja"
        : "en";

  const worthWaiting = !declared || declared.includes(wish);
  if (present.includes(wish) || (!complete && worthWaiting)) {
    return wish;
  }
  if (present.includes("en") || present.length === 0) {
    return "en";
  }
  return LANG_BUCKETS.filter(bucket => present.includes(bucket)).reduce((best, bucket) =>
    (cardsByBucket[bucket] ?? 0) > (cardsByBucket[best] ?? 0) ? bucket : best
  );
};

export interface JapanSignals {
  languages?: readonly string[];
  timeZone?: string;
}

/*
  D4's Japan check, in the browser only — no IP lookup. Either signal is enough: a
  Japanese-language browser anywhere, or any browser whose clock is set to Tokyo.

  The signals are injectable so the tests do not have to fake `navigator`; with none given
  it reads the real ones, and a browser that refuses to answer reads as "not Japan".
*/
export const detectJapan = (signals?: JapanSignals): boolean => {
  let languages = signals?.languages;
  let timeZone = signals?.timeZone;

  if (!signals) {
    try {
      languages =
        typeof navigator !== "undefined"
          ? navigator.languages ?? (navigator.language ? [navigator.language] : [])
          : [];
    } catch (e) {
      languages = [];
    }
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      timeZone = undefined;
    }
  }

  /*
    The browser's FIRST language only. Any "ja" anywhere in the list was too wide: an
    English-speaking browser in New Zealand with Japanese added third (en-US, en-NZ, ja —
    seen in smoke testing, 2026-10-20) opened Pokémon on Japanese. Robert's rule is about
    visitors in Japan, and a Japanese-first browser or a Tokyo clock is what says so.
  */
  const primary = (languages ?? [])[0];
  const japaneseBrowser = typeof primary === "string" && primary.toLowerCase().startsWith("ja");
  return japaneseBrowser || timeZone === "Asia/Tokyo";
};

export const readSavedBucket = (): LangBucket | null => {
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    return isLangBucket(stored) ? stored : null;
  } catch (e) {
    return null;
  }
};

/* Called from the visitor's own click and from nowhere else (D6). */
export const saveBucket = (bucket: LangBucket): void => {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, bucket);
  } catch (e) {
    // Storage unavailable: the choice still holds for this visit through ?lang.
  }
};
